import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { getSlots, type SlotPublic } from "../lib/api";
import { themeStyle, DAY_IMAGE, HBD_IMAGE_THEMES } from "../lib/theme";
import { useHbdTheme } from "../lib/themeContext";
import { localizedDate } from "../../shared/dates";
import {
  EVENT_DAYS,
  ROUND_TIMES,
  MAPS_URL,
  MAPS_EMBED_URL,
  CAFE_NAME,
} from "../../shared/event";
import {
  Cake,
  Gift,
  Trophy,
  Clock,
  MapPin,
  ChevronDown,
  Ticket,
  Heart,
  Sparkles,
  CheckCircle2,
} from "../components/Icon";
import { Balloons } from "../components/decor";
// import { FloatingBalloons } from "../components/decor";

function slotId(date: string, round: number) {
  return `${date}_R${round}`;
}

function Hero() {
  const { t } = useI18n();
  const { index, setIndex } = useHbdTheme();
  return (
    <section className="relative overflow-hidden">
      {/*<FloatingBalloons />*/}
      <div className="relative z-10 container-x grid items-center gap-10 py-14 md:grid-cols-2 md:py-20">
        <div className="text-center md:text-left">
          <span className="eyebrow inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> {t("hero.badge")}
          </span>
          <h1 className="mt-3 whitespace-pre-line font-display text-4xl font-bold leading-[1.1] text-[var(--theme-deep)] sm:text-5xl">
            {t("hero.title")}
          </h1>
          <p className="mt-4 text-lg font-semibold text-ink/80">
            {t("hero.subtitle")}
          </p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-ink/70">
            <MapPin className="h-4 w-4" /> {t("hero.dateRange")} ·{" "}
            {t("hero.venue")}
          </p>
          <p className="mx-auto mt-5 max-w-md leading-relaxed text-ink/70 md:mx-0">
            {t("hero.intro")}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3 md:justify-start">
            <Link to="/reserve" className="btn-primary">
              <Ticket className="h-5 w-5" /> {t("hero.cta")}
            </Link>
            <Link to="/wall" className="btn-ghost">
              <Heart className="h-5 w-5" /> {t("hero.ctaWall")}
            </Link>
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-sm">
          <Balloons className="absolute -left-6 -top-8 h-40 w-32 text-[var(--theme-deep)] opacity-80" />
          <img
            src={HBD_IMAGE_THEMES[index].img}
            alt="HBD Bogummy"
            className="relative mx-auto aspect-square w-72 rounded-full object-cover shadow-soft ring-8 ring-white sm:w-80"
          />
          <div className="relative mt-5 flex flex-wrap justify-center gap-2">
            {HBD_IMAGE_THEMES.map((it, i) => (
              <button
                key={it.img}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Theme ${i + 1}`}
                aria-pressed={i === index}
                className={`h-12 w-12 overflow-hidden rounded-full ring-2 transition ${
                  i === index
                    ? "scale-110 ring-[var(--theme-deep)]"
                    : "opacity-80 ring-white hover:scale-105 hover:opacity-100 hover:ring-[var(--theme-main)]"
                }`}
              >
                <img
                  src={it.img}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ScheduleSection({ remaining }: { remaining: Map<string, number> }) {
  const { t, lang } = useI18n();
  return (
    <section id="schedule" className="container-x py-14">
      <h2 className="section-title text-center">{t("schedule.title")}</h2>
      <p className="mx-auto mt-2 max-w-xl text-center text-ink/65">
        {t("schedule.subtitle")}
      </p>
      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        {EVENT_DAYS.map((day, idx) => (
          <div
            key={day.date}
            style={themeStyle(day.theme)}
            className="card flex flex-col gap-4"
          >
            <div className="flex items-center gap-3">
              <img
                src={DAY_IMAGE[day.theme]}
                alt=""
                className="h-14 w-14 rounded-2xl object-cover ring-2 ring-white"
              />
              <div>
                <div className="eyebrow">Day {idx + 1}</div>
                <div className="font-display text-lg font-bold leading-tight text-ink">
                  {localizedDate(day.date, lang)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {ROUND_TIMES.map((r) => {
                const id = slotId(day.date, r.round);
                const left = remaining.get(id);
                const isFull = left !== undefined && left <= 0;
                return (
                  <Link
                    key={id}
                    to={`/reserve?slot=${id}`}
                    className="flex items-center justify-between rounded-2xl bg-[var(--theme-bg)] px-4 py-2.5 ring-1 ring-black/[0.04] transition hover:ring-[var(--theme-main)]"
                  >
                    <div className="flex items-center gap-2.5">
                      <Clock className="h-4 w-4 text-[var(--theme-deep)]" />
                      <div>
                        <div className="font-semibold text-ink">
                          {r.start}–{r.end}
                        </div>
                        <div className="text-xs text-ink/50">
                          {t("schedule.checkIn")} {r.checkInFrom}
                        </div>
                      </div>
                    </div>
                    {left === undefined ? null : isFull ? (
                      <span className="rounded-full bg-black/5 px-2.5 py-1 text-xs font-bold text-ink/50">
                        {t("common.full")}
                      </span>
                    ) : (
                      <span className="chip text-xs">
                        {t("common.seatsLeft", { n: left })}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>

            <div className="mt-auto flex flex-col gap-2 border-t border-black/[0.06] pt-3 text-sm">
              <span className="inline-flex items-center gap-2 text-ink/70">
                <Gift className="h-4 w-4 text-[var(--theme-deep)]" />{" "}
                {t("schedule.quotaBadgeN", { n: day.giveawayQuota })}
              </span>
              <span className="inline-flex items-center gap-2 text-ink/70">
                <Trophy className="h-4 w-4 text-[var(--theme-deep)]" />{" "}
                {t("schedule.luckyBadge")}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function GiveawaySection() {
  const { t, lang } = useI18n();
  const prizes = [
    { key: "shirt", theme: "day1" as const, date: EVENT_DAYS[0].date },
    { key: "ginseng", theme: "day2" as const, date: EVENT_DAYS[1].date },
    { key: "shampoo", theme: "day3" as const, date: EVENT_DAYS[2].date },
  ];
  return (
    <section
      id="giveaways"
      className="border-y border-black/[0.05] bg-white/70 py-14"
    >
      <div className="container-x">
        <h2 className="section-title text-center">{t("giveaway.title")}</h2>
        <div className="mx-auto mt-8 grid max-w-3xl gap-5 md:grid-cols-2">
          <div className="flex gap-4">
            <span className="icon-badge shrink-0">
              <Gift className="h-6 w-6" />
            </span>
            <div>
              <h3 className="font-bold text-[var(--theme-deep)]">
                {t("giveaway.specialTitle")}
              </h3>
              <p className="mt-1 text-[15px] leading-relaxed text-ink/70">
                {t("giveaway.specialDesc")}
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <span className="icon-badge shrink-0">
              <Trophy className="h-6 w-6" />
            </span>
            <div>
              <h3 className="font-bold text-[var(--theme-deep)]">
                {t("giveaway.luckyTitle")}
              </h3>
              <p className="mt-1 text-[15px] leading-relaxed text-ink/70">
                {t("giveaway.luckyDesc")}
              </p>
            </div>
          </div>
        </div>

        <h3 className="mt-10 text-center text-base font-bold text-ink/60">
          {t("giveaway.perDayTitle")}
        </h3>
        <div className="mx-auto mt-4 grid max-w-3xl gap-4 md:grid-cols-3">
          {prizes.map((p) => (
            <div
              key={p.key}
              style={themeStyle(p.theme)}
              className="rounded-blob bg-[var(--theme-bg)] p-5 text-center ring-1 ring-black/[0.05]"
            >
              <span className="icon-badge mx-auto">
                <Gift className="h-6 w-6" />
              </span>
              <div className="mt-2 text-xs font-bold text-[var(--theme-deep)]">
                {localizedDate(p.date, lang)}
              </div>
              <div className="mt-1 font-semibold text-ink">
                {t(`giveaway.${p.key}`)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NotesSection() {
  const { t, raw } = useI18n();
  const items = raw<string[]>("notes.items");
  return (
    <section id="notes" className="container-x py-14">
      <h2 className="section-title text-center">{t("notes.title")}</h2>
      <ul className="mx-auto mt-7 grid max-w-3xl gap-3 sm:grid-cols-2">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex gap-3 rounded-2xl bg-white px-4 py-3.5 ring-1 ring-black/[0.05]"
          >
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[var(--theme-main)]" />
            <span className="text-[15px] leading-relaxed text-ink/75">
              {it}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FaqSection() {
  const { t, raw } = useI18n();
  const items = raw<{ q: string; a: string }[]>("faq.items");
  return (
    <section
      id="faq"
      className="border-y border-black/[0.05] bg-white/70 py-14"
    >
      <div className="container-x max-w-2xl">
        <h2 className="section-title text-center">{t("faq.title")}</h2>
        <div className="mt-7 space-y-2.5">
          {items.map((it, i) => (
            <details
              key={i}
              className="group rounded-2xl bg-cream px-5 py-4 ring-1 ring-black/[0.06]"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-semibold text-ink">
                {it.q}
                <ChevronDown className="h-5 w-5 shrink-0 text-[var(--theme-deep)] transition group-open:rotate-180" />
              </summary>
              <p className="mt-2.5 text-[15px] leading-relaxed text-ink/70">
                {it.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationSection() {
  const { t } = useI18n();
  return (
    <section id="location" className="container-x py-14">
      <h2 className="section-title text-center">{t("location.title")}</h2>
      <p className="mt-2 text-center text-ink/65">{t("location.desc")}</p>
      <div className="mx-auto mt-7 max-w-3xl overflow-hidden rounded-blob shadow-card ring-1 ring-black/[0.06]">
        <iframe
          title="Gumm&Kalee Café map"
          src={MAPS_EMBED_URL}
          className="h-80 w-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      <div className="mt-5 text-center">
        <a
          href={MAPS_URL}
          target="_blank"
          rel="noreferrer"
          className="btn-primary"
        >
          <MapPin className="h-5 w-5" /> {t("location.button")}
        </a>
        <div className="mt-2 text-sm text-ink/55">{CAFE_NAME}</div>
      </div>
    </section>
  );
}

export function Landing() {
  const { t } = useI18n();
  const [slots, setSlots] = useState<SlotPublic[]>([]);
  useEffect(() => {
    getSlots()
      .then(setSlots)
      .catch(() => setSlots([]));
  }, []);
  const remaining = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of slots) m.set(s.id, s.remaining);
    return m;
  }, [slots]);

  return (
    <>
      <Hero />
      <ScheduleSection remaining={remaining} />
      <GiveawaySection />
      <NotesSection />
      <FaqSection />
      <LocationSection />
      <div className="container-x py-14 text-center">
        <Link to="/reserve" className="btn-primary px-8 py-4 text-lg">
          <Cake className="h-5 w-5" /> {t("hero.cta")}
        </Link>
      </div>
    </>
  );
}
